import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import { ReadTenantsResponse } from 'features/tenants/models/read-tenants-response';
import { TenantAccess } from 'features/tenants/models/tenant-access';
import { UpdateTenantCommand } from 'features/tenants/models/update-tenant-command';

const tenantBaseUrl = '/api/v1/tenant';

enum TenantQueryPaths {
  ReadTenants = 'ReadTenants',
  UpdateTenant = 'UpdateTenant',
  AccessTenant = 'AccessTenant',
}

const readTenants = async (axiosInstance: AxiosInstance): Promise<ReadTenantsResponse> => {
  const response = await axiosInstance.get(`${tenantBaseUrl}/${TenantQueryPaths.ReadTenants}`);
  return response.data;
};

const updateTenant = async (axiosInstance: AxiosInstance, body: UpdateTenantCommand): Promise<void> => {
  await axiosInstance.put(`${tenantBaseUrl}/${TenantQueryPaths.UpdateTenant}`, body);
};

const accessTenant = async (axiosInstance: AxiosInstance, tenantName: string): Promise<TenantAccess> => {
  const response = await axiosInstance.get(`${tenantBaseUrl}/${TenantQueryPaths.AccessTenant}/${tenantName}`);
  return response.data;
};

export const useReadTenants = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [TenantQueryPaths.ReadTenants],
    queryFn: () => readTenants(authenticatedInstance),
  });
};

export const useUpdateTenant = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTenantCommand) => updateTenant(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TenantQueryPaths.ReadTenants] });
    },
  });
};

export const useLazyAccessTenant = (tenantName: string) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [TenantQueryPaths.AccessTenant, tenantName],
    queryFn: () => accessTenant(authenticatedInstance, tenantName),
    enabled: false,
  });
};
